import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { useUpdateEffect } from 'react-use';
import useCropState from '@src/libs/hooks/useCropState';

import Img from '@src/components/Img';
import Slider from '@old/components/common/Slider';
import UILoadmore from '@src/components/UILoadmore';
import PricesNY from '@src/components/TablePricesNY';
import { IconArrBottom } from '@src/components/Icon';
import Options from '@src/components/ListingCardOptions/';
import FiltersHintComp from '@src/components/FiltersHint';
import { ButtonBlack3D as Button } from '@src/components/Button';
import AvailDatePickerComp from '@src/components/AvailDatePicker';

import useStyles from 'isomorphic-style-loader/useStyles';
import css from './style.module.sass';

const VenueRoomDetails = (props) => {
	useStyles(css);
	const {
		title,
		covers,
		params,
		features,
		contentBlocks,
		sliderHeight,
		isPanoramaAvail,
		isDetailsOpen,
		AvailDatePicker,
		isCollapsable,
		isCollapsed,
		filters,
		FiltersHint,
		anchor,
		ShowMoreArrowIcon,
		maxHeight,
		onClickPanorama,
		panoramaSrc,
		onClickImage,
		pickerProps,
		isWithDatepicker,
		pricesNYProps,
	} = props;

	const [isDetailed, toggleDetailed] = React.useState(isDetailsOpen);
	const [isCollapsedState, toggleCollapsed] = React.useState(
		isCollapsed !== undefined ? isCollapsed : true,
	);

	const container = useCropState(maxHeight, !isDetailed, toggleDetailed);

	useUpdateEffect(() => {
		if (isDetailsOpen !== undefined) {
			toggleDetailed(isDetailsOpen);
		}
	}, [isDetailsOpen]);

	useUpdateEffect(() => {
		if (isCollapsed !== undefined) {
			toggleCollapsed(isCollapsed);
		}
	}, [isCollapsed]);

	const handleClickMore = React.useCallback(() => {
		toggleDetailed(!isDetailed);
	}, [toggleDetailed, isDetailed]);

	const handleCollapse = React.useCallback(
		(e) => {
			toggleCollapsed(!e.target.open);
		},
		[toggleCollapsed],
	);

	const handlePanoramaClick = React.useCallback(() => {
		if (onClickPanorama) {
			onClickPanorama(panoramaSrc);
		}
	}, [panoramaSrc, onClickPanorama]);

	const styles = React.useMemo(
		() => ({
			maxHeight: isDetailed ? 'none' : maxHeight,
		}),
		[isDetailed, maxHeight],
	);

	const MainWrapper = isCollapsable ? 'details' : 'div';

	const isOptionsData = params.length || features.length || pricesNYProps;

	return (
		<MainWrapper
			className="grk-card -sizeSectPrfl"
			open={isCollapsable ? !isCollapsedState : undefined}
			onToggle={isCollapsable ? handleCollapse : undefined}
		>
			{anchor}
			{isCollapsable ? (
				<summary className="grk-card-title">
					<span>{title}</span>
					<IconArrBottom className={css.iconArr} />
				</summary>
			) : (
				<div className="grk-card-title">
					<span>{title}</span>
				</div>
			)}
			<div className={cn('prfl-date', css.date)}>
				{filters && <FiltersHint {...filters} />}
				{AvailDatePicker && isWithDatepicker && (
					<AvailDatePicker {...pickerProps} />
				)}
			</div>
			{covers.length > 0 && (
				<Slider
					wrapClass="grk-card-media"
					isReplaceWrapClass
					scrollClass="grk-card-media-scroll"
					height={sliderHeight}
					Footer={
						isPanoramaAvail
							? () => (
									<div className={css.button}>
										<Button onClick={handlePanoramaClick} />
									</div>
							  )
							: null
					}
				>
					{covers.map((cover, i) => {
						return (
							<a
								key={`${cover.src}`}
								tabIndex={0}
								role="button"
								className="grk-card-media-link"
								onClick={
									onClickImage
										? () => onClickImage(covers, i)
										: undefined
								}
							>
								<Img
									{...cover}
									leadHeight={sliderHeight}
									isNoVideo
								/>
							</a>
						);
					})}
				</Slider>
			)}
			<div
				className={cn('grk-card-collapse', {
					'-nonactive': !isDetailed,
				})}
				ref={container}
				style={styles}
			>
				{isOptionsData && (
					<div className={css.options}>
						{pricesNYProps && (
							<PricesNY state="venue" {...pricesNYProps} />
						)}

						{Boolean(params.length) && (
							<Options
								data={params.map(({ name, text }) => ({
									label: name,
									value: text,
								}))}
								state={
									pricesNYProps ? undefined : 'profileVenue'
								}
							/>
						)}

						{Boolean(features.length) && (
							<div className={css.features}>
								{features.map(({ label, options, desc }, i) => (
									// eslint-disable-next-line react/no-array-index-key
									<div className={css.feature} key={i}>
										{label && (
											<div className={css.featureLabel}>
												{label}
											</div>
										)}

										{Boolean(options.length) && (
											<Options
												state={
													features.length === 1 &&
													!pricesNYProps
														? 'profileVenue'
														: undefined
												}
												data={options}
											/>
										)}

										{desc}
									</div>
								))}
							</div>
						)}
					</div>
				)}

				{contentBlocks.map((block) => {
					const { name, text } = block;
					const textLocal = pricesNYProps ? `<p>${text}</p>` : text;

					return (
						<div
							className={css.desc}
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: name
									? `<b>${name}</b> ${textLocal}`
									: text,
							}}
							key={name}
						/>
					);
				})}

				{!isDetailed && (
					<div className={css.loadmore}>
						<UILoadmore
							label={t('Подробнее')}
							Icon={ShowMoreArrowIcon}
							onClick={handleClickMore}
						/>
					</div>
				)}
			</div>
		</MainWrapper>
	);
};

VenueRoomDetails.propTypes = {
	/**
	 * Заголовок карточки.
	 */
	title: PropTypes.string,
	/**
	 * Контроллер наличия/отсутствия блока "Подходит для вас".
	 */
	isSuit: PropTypes.bool,
	/**
	 * Контроллер наличия/отсутствия трехмерной панорамы (кнопка "3D-обзор").
	 */
	isPanoramaAvail: PropTypes.bool,
	/**
	 * Контроллер открытого/закрытого блока с подробной информацией. (Не путать с
	 * тэгом `details`).
	 */
	isDetailsOpen: PropTypes.bool,
	/**
	 * Контроллер наличия/отсутствия возможности сворачивать карточку. (Тэг
	 * `details`).
	 */
	isCollapsable: PropTypes.bool,
	/**
	 * Контроллер изначального состояния карточки (свернута/развернута).
	 */
	isCollapsed: PropTypes.bool,
	/**
	 * Высота (в `px`) блока прокручивающихся фотографий.
	 */
	sliderHeight: PropTypes.number,
	/**
	 * Текст блока "Не подходит".
	 */
	nonFitLabel: PropTypes.string,
	/**
	 * Массив объектов, содержащих параметры обложек фотографий карточки.
	 */
	covers: PropTypes.arrayOf(PropTypes.shape({ ...Img.type.propTypes })),
	/**
	 * Массив объектов, содержащих текстовую информацию о зале (контентные блоки).
	 * Где:
	 * *name* — название контентного блока;
	 * *text* — текст контентного блока.
	 */
	contentBlocks: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			text: PropTypes.string,
		}),
	),
	/**
	 * Массив объектов, содержащих информацию о параметрах зала (табличный блок).
	 * Где:
	 * *name* — имя параметра;
	 * *text* — значение параметра.
	 */
	params: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			text: PropTypes.string,
		}),
	),
	features: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			options: PropTypes.arrayOf(PropTypes.shape(Options.type.propTypes)),
			desc: PropTypes.string,
		}),
	),
	/**
	 * Объект содержащий информацию о фильтрах для зала.
	 */
	filters: PropTypes.shape({ ...FiltersHintComp.type.propTypes }),
	/**
	 * HTML-элемент, якорь прямой ссылки на карточку.
	 */
	anchor: PropTypes.element,
	/**
	 * React-компонент, блок выбора даты.
	 */
	AvailDatePicker: PropTypes.elementType,
	/**
	 * React-компонент, блок фильтров для помещения.
	 */
	FiltersHint: PropTypes.elementType,
	/**
	 * React-компонент, иконка кнопки "Подробнее".
	 */
	ShowMoreArrowIcon: PropTypes.elementType,
	/**
	 * Максимальная высота блока `grk-card-collapse`.
	 */
	maxHeight: PropTypes.number,
	/**
	 * Функция-обработчик клика на бирку панорамы. В качестве параметра принимает пропс panoramaSrc
	 */
	onClickPanorama: PropTypes.func,
	/**
	 * Ссылка на панораму
	 */
	panoramaSrc: PropTypes.string,
	/**
	 * Обработчик клика на изображение
	 */
	onClickImage: PropTypes.func,
	/**
	 * Параметры для AvailDatePicker
	 */
	pickerProps: PropTypes.shape({ ...AvailDatePickerComp.type.propTypes }),
	isWithDatepicker: PropTypes.bool,
	pricesNYProps: PropTypes.shape(PricesNY.type.propTypes),
};

VenueRoomDetails.defaultProps = {
	title: undefined,
	isSuit: false,
	isPanoramaAvail: false,
	isCollapsable: false,
	isDetailsOpen: undefined,
	isCollapsed: undefined,
	pickerProps: undefined,
	covers: [],
	params: [],
	features: [],
	contentBlocks: [],
	sliderHeight: 248,
	nonFitLabel: undefined,
	AvailDatePicker: undefined,
	FiltersHint: FiltersHintComp,
	anchor: null,
	filters: undefined,
	ShowMoreArrowIcon: () => <IconArrBottom />,
	maxHeight: 300,
	onClickPanorama: undefined,
	panoramaSrc: undefined,
	onClickImage: undefined,
	isWithDatepicker: true,
	pricesNYProps: undefined,
};

export default VenueRoomDetails;
